import { useAppRouter } from '@/common/route/useAppRouter';
import { CURRENT_TIME_STAMP, validateDateAfter, validateFormat } from '@/common/validator/validatorHelpers';
import { useCrudActions } from '@/common/view/useCrudActions';
import { ROUTE_NAME_ARENA } from '@/modules/arena/router';
import { arenaService } from '@/services/arena';
import { and, integer, maxValue, minValue, required } from '@vuelidate/validators';
import { reactive } from 'vue';
import { useRoute } from 'vue-router';

export function useArenaItemMixins() {
    const route = useRoute();
    const router = useAppRouter();

    const model = reactive({
        id: route.params?.id || null,
        name: null,
        matchId: null,
        matchName: null,
        maxRejection: null,
        price: null,
        jackpotInit: null,
        jackpotWinPercent: null,
        jackpotDonation: null,
        prizes: null,
        status: null,
        ticketType: null,
        timeUnit: 0,
        timeDuration: 0,
        publishSeconds: 0,
        startSeconds: 0,
        closeSeconds: 0,
        endSeconds: 0,
        publishTime: new Date(),
        unpublishTime: null,
        nextCycle: null,

        canEnable: false,
        canDisable: false,
        canDelete: false,
        canEdit: false,
        canClone: false
    });

    const updateValidator = {
        name: { required },
        publishSeconds: { required, min: minValue(1) },
        startSeconds: { required, min: minValue(1) },
        closeSeconds: { required, min: minValue(1) },
        endSeconds: { required, min: minValue(1) },
        unpublishTime: { validateDate: and(validateDateAfter(CURRENT_TIME_STAMP), validateDateAfter('publishTime')) }
    };

    const creationValidator = {
        ...updateValidator,
        matchId: { required },
        ticketType: { required },
        price: { required, integer, min: minValue(0) },
        maxRejection: { required, integer, min: minValue(0) },
        jackpotInit: { required, integer, min: minValue(0) },
        jackpotWinPercent: { required, integer, min: minValue(0) },
        jackpotDonation: { required, integer, min: minValue(0), max: maxValue(100) },
        prizes: { required, format: validateFormat(/^\d+(,\d+)*$/) },
        timeUnit: { required },
        timeDuration: { required, min: minValue(1) },
        publishTime: { required, validateDate: validateDateAfter() },
        unpublishTime: { validateDate: validateDateAfter('publishTime') }
    };

    const fetch = useCrudActions().executeAction({
        request: () => arenaService.item.get(route.params.id),
        onSuccess: ({ data }) => {
            data.publishTime = new Date(data.publishTime);
            data.unpublishTime = data.unpublishTime ? new Date(data.unpublishTime) : null;

            if (data.nextCycle != null) {
                data.nextCycle.publishTime = new Date(data.nextCycle.publishTime);
                data.nextCycle.startTime = new Date(data.nextCycle.startTime);
                data.nextCycle.closeTime = new Date(data.nextCycle.closeTime);
                data.nextCycle.endTime = new Date(data.nextCycle.endTime);
                data.nextCycle.unpublishTime = new Date(data.nextCycle.unpublishTime);
                data.nextCycle.archiveTime = new Date(data.nextCycle.archiveTime);
            }

            Object.assign(model, data);
        },
        onError: () => router.push({ name: ROUTE_NAME_ARENA.ITEM.INDEX })
    });

    return { model, creationValidator, updateValidator, fetch };
}

export const ArenaStatusList = [
    { label: 'None', value: 0, background: 'secondary' },
    { label: 'Enabled', value: 1, background: 'success' },
    { label: 'Published', value: 2, background: 'info' },
    { label: 'Unpublished', value: 3, background: 'warning' },
    { label: 'Disabled', value: 4, background: 'danger' }
];
